import React, { useEffect, useRef, useState } from 'react';
import Config from '../../helpers/config/GanttChartConfigs';
import './GanttChartDataTask.scss';
import { GanttChartItem } from '../../models/GanttChartItem.model';
import { DAY_WIDTH, FIRST_COLUMN_GAP } from '../../GanttChartConstants';

export interface GanttChartDataTask {
    id: string;
    backgroundColor: string;
    color: string;
    left: number;
    width: number;
    height: number;
    item: GanttChartItem;
    onClick(item: GanttChartItem): void;
    className?: string;
    chartConteinarWidth: number;
    chartScrollLeft: number;
    isTaskClickable?: (taskId: string) => boolean;
}
export const GanttChartDataTask = (props: GanttChartDataTask) => {
    const textRef = useRef<HTMLDivElement | null>(null);
    const [firstRender, setFirstRender] = useState(true);
    const [textWidth, setTextWidth] = useState(0);
    const [textMargin, setTextMargin] = useState(0);

    useEffect(() => {
        if (textRef.current) {
            setTextWidth(textRef.current.offsetWidth);
            calculateTextMargin();
        }
    }, [
        props.item.name,
        props.chartScrollLeft,
        firstRender,
        props.chartConteinarWidth,
    ]);

    const calculateStyle = () => {
        const { backgroundColor: configBg, ...restConfigStyle } = Config.values.dataViewPort.task.style;
        const background = props.backgroundColor ? props.backgroundColor : configBg;
        const color = props.color ? props.color : restConfigStyle.color;

        return {
            ...restConfigStyle,
            background,
            color,
            left: props.left,
            width: props.width,
            height: props.height,
            top: 0,
        };
    };

    let style = calculateStyle();
    const itemName = Config.values.dataViewPort.task.showLabel
        ? props.item.name
        : '';

    const calculateTextMargin = () => {
        let margin = 0;
        let visiblePart = 0;
        const { width, left, chartConteinarWidth, chartScrollLeft } = props;
        const adjustedLeft = left - DAY_WIDTH * FIRST_COLUMN_GAP;
        const adjustedContainerWidth =
            chartConteinarWidth - DAY_WIDTH * FIRST_COLUMN_GAP;

        if (
            width > adjustedContainerWidth &&
            adjustedLeft < chartScrollLeft &&
            chartScrollLeft < adjustedLeft + width - adjustedContainerWidth
        ) {
            margin =
                chartScrollLeft -
                adjustedLeft +
                (adjustedContainerWidth - textWidth) / 2;
        } else {
            if (adjustedLeft > chartScrollLeft) {
                visiblePart = Math.min(
                    adjustedContainerWidth,
                    chartConteinarWidth - left + chartScrollLeft,
                    width
                );
                margin =
                    visiblePart < textWidth ? 0 : (visiblePart - textWidth) / 2;
            } else {
                visiblePart = width - (chartScrollLeft - adjustedLeft);
                margin =
                    visiblePart < textWidth
                        ? width - textWidth
                        : width - visiblePart + (visiblePart - textWidth) / 2;
            }
        }

        if (textWidth === 0) {
            setFirstRender(false);
        }
        setTextMargin(margin);
    };

    const textStyle =
        textWidth + 5 <= props.width
            ? {
                  display: 'inline-block',
                  width: 'fit-content',
                  marginLeft: textMargin,
              }
            : {
                  display: 'inline-block',
                  width: 'max-content',
                  overflow: 'visible' as const,
              };

    const isTaskClickable = !props.isTaskClickable
        ? true
        : props.isTaskClickable(props.item.id.slice(6));

    return (
        <div style={style as any} id={props.id}>
            <div
                ref={textRef}
                onClick={() => {
                    if (isTaskClickable) {
                        props.onClick(props.item);
                    }
                }}
                style={{
                    overflow: 'hidden',
                    ...textStyle,
                }}
                className={`${
                    props.className ? props.className : ''
                } gantt-chart-data-task-text`}
                title={itemName}>
                {itemName}
            </div>
        </div>
    );
};
